import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { MdArrowBack, MdArrowForward } from "react-icons/md";

const MemberInsightsSection = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [apiError, setApiError] = useState(false);
  const [autoPlay, setAutoPlay] = useState(true);
  const [itemsPerRow, setItemsPerRow] = useState(2); // Default 2 items per row
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const API_URL = `${API_BASE}/UImember-insights`;
  const API_MEDIA = `${API_BASE}/UImedia`;

  // Auto-rotate interval (5 seconds)
  const AUTO_ROTATE_INTERVAL = 5000;

  // ✅ Extract YouTube Video ID
  const extractYouTubeId = (url) => {
    if (!url || typeof url !== 'string') return "";

    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    return "";
  };

  // ✅ Create proper YouTube URL
  const getYouTubeUrl = (videoId) => {
    if (!videoId) return "";
    return `https://www.youtube.com/watch?v=${videoId}`;
  };

  // ✅ Open YouTube video properly
  const openYouTubeVideo = (youtubeVideoUrl, videoId) => {
    try {
      const url = videoId ? getYouTubeUrl(videoId) : youtubeVideoUrl;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Error opening YouTube video:', error);
    }
  };

  const fetchImageWithAuth = useCallback(async (photoId) => {
    try {
      const response = await axios.get(`${API_MEDIA}/${photoId}/download`, {
        responseType: "blob",
      });
      return URL.createObjectURL(response.data);
    } catch (err) {
      return "https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-4.0.3&auto=format&fit=crop&w=256&h=256&q=80";
    }
  }, [API_MEDIA]);

  // ✅ DIRECT TESTIMONIALS DATA
  const defaultTestimonials = [
    {
      _id: "1",
      name: "Kanika Gupta",
      text: "I think this a perfect platform for women to come together and grow together. Do something very substantial",
      profileImage: "https://res.cloudinary.com/dnmlijyox/image/upload/testimonial-imgs/kanika.webp",
      designation: "Member",
      organization: "Community Platform",
      youtubeVideoUrl: ""
    },

  ];

  // Responsive items per row
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      const tablet = window.innerWidth >= 768 && window.innerWidth < 1024;

      setIsMobile(mobile);
      setIsTablet(tablet);

      if (window.innerWidth >= 1024) {
        setItemsPerRow(2); // Desktop: 2 items per row
      } else if (window.innerWidth >= 768) {
        setItemsPerRow(2); // Tablet: 2 items per row
      } else {
        setItemsPerRow(1); // Mobile: 1 item per row
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchMembers = useCallback(async () => {
    try {
      setLoading(true);
      setApiError(false);

      const res = await axios.get(API_URL, { timeout: 5000 });

      let membersData = res.data.results || res.data || [];
      membersData = membersData.filter(member => member.isActive !== false);

      if (membersData.length === 0) {
        setMembers(defaultTestimonials);
        setApiError(true);
      } else {
        const formattedMembers = await Promise.all(
          membersData.map(async (member) => {
            let profileImage = "https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-4.0.3&auto=format&fit=crop&w=256&h=256&q=80";

            if (member.photo?.url) {
              profileImage = member.photo.url;
            } else if (member.photo?._id || member.photo?.id || member.photo) {
              const photoId = member.photo._id || member.photo.id || member.photo;
              if (typeof photoId === 'string') {
                profileImage = await fetchImageWithAuth(photoId);
              }
            }

            let text = member.quote || "";

            if (!text || text.trim() === "") {
              if (member.designation && member.organization) {
                text = `${member.designation} at ${member.organization}`;
              } else if (member.designation) {
                text = member.designation;
              } else if (member.organization) {
                text = `Works at ${member.organization}`;
              } else {
                text = "Team Member";
              }
            }

            const youtubeVideoUrl = member.youtubeVideoUrl || "";
            const videoId = extractYouTubeId(youtubeVideoUrl);
            const hasVideo = !!videoId;

            return {
              _id: member._id || member.id || Date.now().toString(),
              name: member.memberName || "Team Member",
              designation: member.designation || "",
              organization: member.organization || "",
              profileImage: profileImage,
              text: text,
              quote: member.quote || "",
              youtubeVideoUrl: youtubeVideoUrl,
              hasVideo: hasVideo,
              videoId: videoId,
              properYoutubeUrl: hasVideo ? getYouTubeUrl(videoId) : ""
            };
          })
        );

        setMembers(formattedMembers);
      }

    } catch (error) {
      console.error("Error fetching members from API:", error);
      setMembers(defaultTestimonials);
      setApiError(true);
    } finally {
      setLoading(false);
    }
  }, [API_URL, fetchImageWithAuth]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // Auto-rotate testimonials every 5 seconds
  useEffect(() => {
    if (!autoPlay || members.length <= itemsPerRow) return;

    const interval = setInterval(() => {
      setDirection(1);
      const maxIndex = Math.max(0, members.length - itemsPerRow);
      setCurrentIndex((prev) => prev >= maxIndex ? 0 : prev + 1);
    }, AUTO_ROTATE_INTERVAL);

    return () => clearInterval(interval);
  }, [autoPlay, members.length, itemsPerRow, currentIndex]);

  const testimonialVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
      scale: 0.9,
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeInOut",
      }
    },
    exit: (direction) => ({
      x: direction > 0 ? -300 : 300,
      opacity: 0,
      scale: 0.9,
      transition: {
        duration: 0.5,
        ease: "easeInOut"
      }
    })
  };

  const nextSlide = () => {
    if (members.length <= itemsPerRow) return;

    const maxIndex = Math.max(0, members.length - itemsPerRow);
    setDirection(1);
    setCurrentIndex((prev) => prev >= maxIndex ? 0 : prev + 1);
    setAutoPlay(false);

    setTimeout(() => setAutoPlay(true), 10000);
  };

  const prevSlide = () => {
    if (members.length <= itemsPerRow) return;

    const maxIndex = Math.max(0, members.length - itemsPerRow);
    setDirection(-1);
    setCurrentIndex((prev) => prev === 0 ? maxIndex : prev - 1);
    setAutoPlay(false);

    setTimeout(() => setAutoPlay(true), 10000);
  };

  const goToSlide = (index) => {
    const direction = index > currentIndex ? 1 : -1;
    setDirection(direction);
    setCurrentIndex(index);
    setAutoPlay(false);

    setTimeout(() => setAutoPlay(true), 10000);
  };

  // Get current visible members
  const getVisibleMembers = () => {
    return members.slice(currentIndex, currentIndex + itemsPerRow);
  };

  if (loading && members.length === 0) {
    return (
      <section className="py-12 sm:py-16 bg-gray-800 px-4">
        <h2 className="text-2xl sm:text-3xl font-bold text-center text-white mb-8 sm:mb-12">
          Member Insights
        </h2>
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="w-10 h-10 border-4 border-white border-t-amber-500 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-white">Loading member insights...</p>
          </div>
        </div>
      </section>
    );
  }

  if (!loading && members.length === 0) {
    return (
      <section className="py-12 sm:py-16 bg-gray-800 px-4">
        <h2 className="text-2xl sm:text-3xl font-bold text-center text-white mb-8 sm:mb-12">
          Member Insights
        </h2>
        <div className="flex justify-center">
          <p className="text-gray-300">No member insights available yet.</p>
        </div>
      </section>
    );
  }

  const visibleMembers = getVisibleMembers();

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="py-12 sm:py-20 bg-gray-800 px-4 sm:px-6 lg:px-8"
    >
      <h2 className="text-2xl sm:text-4xl font-bold text-center text-white mb-8 sm:mb-16">
        Member Insights
      </h2>

      <div className="flex items-center justify-center gap-2 sm:gap-8 mb-8">
        <button
          onClick={prevSlide}
          className="p-3 sm:p-4 bg-gray-700 rounded-full hover:bg-amber-600 transition-all duration-300 shadow-lg hover:shadow-amber-500/30 transform hover:-translate-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Previous testimonial"
          disabled={members.length <= itemsPerRow}
        >
          <MdArrowBack className="text-2xl sm:text-3xl text-white" />
        </button>

        <div className="relative w-full max-w-6xl min-h-[400px] sm:min-h-[450px] flex items-center justify-center">
          <AnimatePresence initial={false} custom={direction} mode="wait">
            <motion.div
              key={currentIndex}
              custom={direction}
              variants={testimonialVariants}
              initial="enter"
              animate="center"
              exit="exit"
              className="absolute w-full"
            >
              <div className={`grid grid-cols-1 ${itemsPerRow === 2 ? 'md:grid-cols-2' : ''} gap-6 sm:gap-8 lg:gap-10`}>
                {visibleMembers.map((member, idx) => (
                  <motion.div
                    key={member._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className={`bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden h-full ${itemsPerRow === 1 ? 'max-w-2xl mx-auto' : ''
                      }`}
                  >
                    <div className="p-6 sm:p-8 h-full flex flex-col">
                      {/* Mobile View: Video at bottom center after text */}
                      {isMobile && member.hasVideo && member.videoId ? (
                        <>
                          {/* Top Section: Image and Name only for mobile */}
                          <div className="flex items-start gap-4 mb-6">
                            {/* Profile Image */}
                            <div className="flex-shrink-0">
                              <div className="relative">
                                <img
                                  src={member.profileImage}
                                  alt={member.name}
                                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-3 border-amber-500"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-4.0.3&auto=format&fit=crop&w=256&h=256&q=80";
                                  }}
                                />
                                <div className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1.5">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                                  </svg>
                                </div>
                              </div>
                            </div>

                            {/* Name and Designation */}
                            <div className="flex-1">
                              <h3 className="text-gray-900 font-bold text-lg sm:text-xl mb-1">
                                {member.name}
                              </h3>
                              {member.designation && (
                                <p className="text-amber-600 font-medium text-sm sm:text-base">
                                  {member.designation}
                                  {member.organization && ` • ${member.organization}`}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Quote/Testimonial */}
                          <div className="mb-6 flex-1">
                            <div className="relative">
                              <div className="absolute -top-4 -left-4 text-4xl text-amber-200 opacity-70">"</div>
                              <p className="text-gray-700 text-base sm:text-lg leading-relaxed italic min-h-[0px]">
                                {member.text}
                              </p>
                              <div className="absolute -bottom-4 -right-4 text-4xl text-amber-200 opacity-70">"</div>
                            </div>
                          </div>

                          {/* Video Box - Center aligned for mobile */}
                          <div className="mt-4 flex justify-center">
                            <div
                              className="relative w-40 h-24 rounded-lg overflow-hidden cursor-pointer group"
                              onClick={() => openYouTubeVideo(member.youtubeVideoUrl, member.videoId)}
                            >
                              <img
                                src={`https://img.youtube.com/vi/${member.videoId}/mqdefault.jpg`}
                                alt="YouTube Video"
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                              />
                              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center group-hover:bg-red-700 transition-colors">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white ml-0.5" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M8 5v14l11-7z" />
                                  </svg>
                                </div>
                              </div>
                              <div className="absolute bottom-2 left-2 right-2 text-xs text-white text-center bg-black/50 py-1 rounded">
                                Watch Video
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        /* Desktop/Tablet View with conditional layout */
                        <>
                          {/* Top Section: Image, Name, and Video */}
                          <div className={`flex items-start gap-4 mb-6 ${itemsPerRow === 1 ? 'justify-center text-center' : ''}`}>
                            {/* Profile Image */}
                            <div className="flex-shrink-0">
                              <div className="relative">
                                <img
                                  src={member.profileImage}
                                  alt={member.name}
                                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-3 border-amber-500"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-4.0.3&auto=format&fit=crop&w=256&h=256&q=80";
                                  }}
                                />
                                {member.hasVideo && (
                                  <div className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1.5">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Name and Designation */}
                            <div className={`flex-1 ${itemsPerRow === 1 ? 'text-center' : ''}`}>
                              <h3 className="text-gray-900 font-bold text-lg sm:text-xl mb-1">
                                {member.name}
                              </h3>
                              {member.designation && (
                                <p className="text-amber-600 font-medium text-sm sm:text-base">
                                  {member.designation}
                                  {member.organization && ` • ${member.organization}`}
                                </p>
                              )}
                            </div>

                            {/* Video Box (50x50px) - Only show when itemsPerRow === 2 */}
                            {itemsPerRow === 2 && member.hasVideo && member.videoId && (
                              <div className="flex-shrink-0">
                                <div
                                  className="relative w-12 h-12 sm:w-14 sm:h-14 rounded-lg overflow-hidden cursor-pointer group"
                                  style={{
                                    width: '10.5rem',
                                    height: '5.5rem'
                                  }}
                                  onClick={() => openYouTubeVideo(member.youtubeVideoUrl, member.videoId)}
                                >
                                  <img
                                    src={`https://img.youtube.com/vi/${member.videoId}/mqdefault.jpg`}
                                    alt="YouTube Video"
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                  />
                                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                    <div className="w-6 h-6 sm:w-7 sm:h-7 bg-red-600 rounded-full flex items-center justify-center group-hover:bg-red-700 transition-colors">
                                      <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white ml-0.5" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M8 5v14l11-7z" />
                                      </svg>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Quote/Testimonial */}
                          <div className="mb-6 flex-1">
                            <div className="relative">
                              <div className={`absolute -top-4 ${itemsPerRow === 1 ? '-left-8' : '-left-4'} text-4xl text-amber-200 opacity-70`}>"</div>
                              <p className="text-gray-700 text-base sm:text-lg leading-relaxed italic min-h-[0px] text-center">
                                {member.text}
                              </p>
                              <div className={`absolute -bottom-4 ${itemsPerRow === 1 ? '-right-8' : '-right-4'} text-4xl text-amber-200 opacity-70`}>"</div>
                            </div>
                          </div>

                          {/* Video Box for Single Item (Desktop/Tablet) */}
                          {itemsPerRow === 1 && member.hasVideo && member.videoId && (
                            <div className="mt-6 flex justify-center">
                              <div
                                className="relative w-48 h-32 rounded-lg overflow-hidden cursor-pointer group"
                                onClick={() => openYouTubeVideo(member.youtubeVideoUrl, member.videoId)}
                              >
                                <img
                                  src={`https://img.youtube.com/vi/${member.videoId}/hqdefault.jpg`}
                                  alt="YouTube Video"
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                />
                                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center group-hover:bg-red-700 transition-colors">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white ml-0.5" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M8 5v14l11-7z" />
                                    </svg>
                                  </div>
                                </div>
                                <div className="absolute bottom-2 left-2 right-2 text-xs text-white text-center bg-black/50 py-1 rounded">
                                  Watch Video
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <button
          onClick={nextSlide}
          className="p-3 sm:p-4 bg-gray-700 rounded-full hover:bg-amber-600 transition-all duration-300 shadow-lg hover:shadow-amber-500/30 transform hover:translate-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Next testimonial"
          disabled={members.length <= itemsPerRow}
        >
          <MdArrowForward className="text-2xl sm:text-3xl text-white" />
        </button>
      </div>

      {/* Navigation dots */}
      {members.length > itemsPerRow && (
        <div className="flex justify-center items-center gap-2 sm:gap-3 mt-8 sm:mt-12">
          {Array.from({ length: Math.max(1, members.length - itemsPerRow + 1) }).map((_, idx) => {
            const hasVideoInGroup = members
              .slice(idx, idx + itemsPerRow)
              .some(member => member.hasVideo);

            return (
              <button
                key={idx}
                onClick={() => goToSlide(idx)}
                className="relative group"
                aria-label={`Go to insights ${idx + 1}`}
              >
                <div
                  className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full transition-all duration-300 ${currentIndex === idx
                      ? (hasVideoInGroup ? 'bg-red-500 scale-125' : 'bg-amber-500 scale-125')
                      : (hasVideoInGroup ? 'bg-red-400/50 group-hover:bg-red-300' : 'bg-gray-600 group-hover:bg-gray-500')
                    }`}
                />

                {/* Active indicator ring */}
                {currentIndex === idx && (
                  <div className="absolute -inset-1 border border-amber-500/50 rounded-full" />
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Current position indicator */}
      {members.length > itemsPerRow && (
        <div className="text-center mt-4 sm:mt-6 text-gray-400 text-sm sm:text-base">
          <span className="font-medium text-amber-400">
            Showing {Math.min(currentIndex + itemsPerRow, members.length)} of {members.length} insights
          </span>
        </div>
      )}
    </motion.section>
  );
};

export default MemberInsightsSection;